import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import {
  getDefaultEmailTemplate,
  renderPlaceholdersInTemplates
} from 'shared-helpers/email';
import { useApiData } from 'lib/api';
import { ModalButtonWithForm } from 'components/common';
import { SelectAdmin } from 'components/common/admins';
import {
  EmailMessageEditor,
  FormGroup,
  RadioGroup
} from 'components/common/form';
import { AdminFromSelect } from 'components/common/admins/SelectAdmin';

import type { AxiosResponse } from 'axios';
import type { FormState } from 'informed';
import type { EmailMessageTemplateContent } from 'shared-types';
import type { AddAchievementSupervisorReq } from 'shared-types/requests';
import type { GetEmailTemplatesByTypeRes } from 'shared-types/responses';

type FormValues = {
  email: EmailMessageTemplateContent;
  sendNotification: boolean;
  supervisorIds: AdminFromSelect[];
};

interface Props {
  achievementId: number;
  existingSupervisorIds: number[];
  updateData: () => Promise<void>;
}

const AchievementSupervisorsAdd = ({
  achievementId,
  existingSupervisorIds,
  updateData
}: Props) => {
  const { dispatch } = useDispatch();

  const {
    language,
    messages,
    pageState: { achievement },
    serverConfig: { adminUrl },
    user
  } = useReduxState();

  const emailType = {
    conversation: 'achievement-supervisor-added-to-conversation-notes',
    external: 'achievement-supervisor-added-to-achievement',
    'group-conversation': 'achievement-supervisor-added-to-group-conversation',
    milestone: 'achievement-supervisor-added-to-milestone',
    tasklist: 'achievement-supervisor-added-to-task-list'
  }[achievement.type];

  const { data: emailTemplatesData } = useApiData<GetEmailTemplatesByTypeRes>({
    url: `/admin/email-templates-data?type=${emailType}&achievementId=${achievement.id}`
  });

  let emailTemplates = emailTemplatesData?.emailTemplates || [];

  emailTemplates = renderPlaceholdersInTemplates({
    emailTemplates,
    overridePlaceholderValues: {
      contactEmail: user?.email,
      achievementSupervisorUrl: `${adminUrl}/admin/achievements/${achievement.id}/my-team`,
      achievementTitle: achievement.title
    }
  });

  const emailTemplatesDefault = getDefaultEmailTemplate({
    language,
    emailTemplates
  });

  const handleSubmit = async (values: FormValues) => {
    try {
      const response = await api.post<
        unknown,
        AxiosResponse<
          | {
              errors: {
                alreadyAchievementSupervisor: string[];
                missingSupervisors: string[];
              };
              success: true;
            }
          | { error: string | boolean; success: never }
        >
      >(`/admin/achievements/${achievementId}/supervisors`, {
        emailContent: values.email,
        sendEmail: values.sendNotification,
        userIds: values.supervisorIds?.map((supervisor) => supervisor.userId)
      } as AddAchievementSupervisorReq);

      if (response.data.success) {
        dispatch.addNotice({
          text: messages.adminSupervisorsAddSuccess,
          type: 'success'
        });

        if (response.data.errors?.missingSupervisors?.length) {
          const text =
            `${messages.supervisorWarningUserNotFound}` +
            '\n' +
            response.data.errors.missingSupervisors.join('\n');
          dispatch.addNotice({ text, type: 'warning' });
        }

        if (response.data.errors?.alreadyAchievementSupervisor?.length) {
          const text =
            `${messages.achievementSupervisorAddExistingWarning}` +
            '\n' +
            response.data.errors.alreadyAchievementSupervisor.join('\n');
          dispatch.addNotice({ text, type: 'warning' });
        }

        await updateData();
      }
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const initialValues = emailTemplatesDefault
    ? {
        email: {
          ...emailTemplatesDefault.email,
          selectedTemplateIdentifier: emailTemplatesDefault.identifier
        },
        sendNotification: true
      }
    : { sendNotification: false };

  return (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonIcon="plus"
      buttonSize="header"
      buttonText={messages.adminSupervisorsAdd}
      initialValues={initialValues}
      modalActionText={messages.commonAdd}
      modalTitle={messages.adminSupervisorsAdd}
      modalWidth="wide"
      onSubmit={handleSubmit}
    >
      {({ values }: FormState<FormValues>) => (
        <>
          <FormGroup label={messages.adminSupervisors}>
            <SelectAdmin
              existingUserIds={existingSupervisorIds}
              field="supervisorIds"
              isClearable={false}
              isMulti={true}
              isRequired={true}
              placeholder={messages.settingsSearchForUser}
              searchParams="&role=supervisor"
            />
          </FormGroup>

          <FormGroup label={messages.emailNotification} maxWidth="100%">
            <RadioGroup
              field="sendNotification"
              options={[
                {
                  id: 'notification-send',
                  label: messages.emailNotificationAllAbove,
                  value: true
                },
                {
                  id: 'notification-not-send',
                  label: messages.emailNotificationNo,
                  value: false
                }
              ]}
            />

            {values.sendNotification ? (
              <EmailMessageEditor
                availableTemplates={emailTemplates}
                id="supervisors-add"
                style={{ marginTop: 20 }}
              />
            ) : null}
          </FormGroup>
        </>
      )}
    </ModalButtonWithForm>
  );
};

export default AchievementSupervisorsAdd;
